import React, { useEffect, useMemo, useState } from "react";
import type { IntellioTaskRecord } from "./types.js";

export interface IntellioTaskShelfProps {
  activeTasks: IntellioTaskRecord[];
  recentTasks: IntellioTaskRecord[];
  completedCollapsed?: boolean;
  onToggleCompletedCollapsed?: () => void;
  onCancelTask?: (taskId: string) => void;
  onConfirmTask?: (taskId: string) => void;
}

const progressTone = (state: string): string => {
  switch (state) {
    case "completed":
      return "from-jungle to-primary";
    case "failed":
    case "cancelled":
      return "from-strawberry to-[#ff8a5b]";
    default:
      return "from-primary to-jungle";
  }
};

const cardTone = (task: IntellioTaskRecord): string => {
  if (task.isNew) {
    return "border-primary/35 bg-[linear-gradient(135deg,rgba(242,252,248,0.98),rgba(255,255,255,0.98))] shadow-[0_24px_55px_rgba(38,151,162,0.14)]";
  }
  if (task.isOptimistic) {
    return "border-primary/20 bg-[linear-gradient(135deg,rgba(247,252,251,0.98),rgba(255,255,255,0.98))]";
  }
  return "border-gray-200 bg-white/95";
};

const TASK_SHELF_COMPACT_BREAKPOINT = 1024;

export const IntellioTaskShelf: React.FC<IntellioTaskShelfProps> = ({
  activeTasks,
  recentTasks,
  completedCollapsed = false,
  onToggleCompletedCollapsed,
  onCancelTask,
  onConfirmTask,
}) => {
  const [selectedTaskId, setSelectedTaskId] = useState<string | null>(null);
  const [isCompactViewport, setIsCompactViewport] = useState(false);
  const [showCompactSheet, setShowCompactSheet] = useState(false);
  const prioritizedActiveTasks = useMemo(
    () =>
      [...activeTasks].sort(
        (left, right) =>
          Number(Boolean(right.isNew)) - Number(Boolean(left.isNew)),
      ),
    [activeTasks],
  );
  const selectedTask = useMemo(
    () =>
      [...prioritizedActiveTasks, ...recentTasks].find(
        (task) => task.id === selectedTaskId,
      ) ?? null,
    [prioritizedActiveTasks, recentTasks, selectedTaskId],
  );

  useEffect(() => {
    if (typeof window === "undefined") {
      return () => {};
    }
    const media = window.matchMedia(
      `(max-width: ${TASK_SHELF_COMPACT_BREAKPOINT}px)`,
    );
    const update = () => setIsCompactViewport(media.matches);
    update();
    media.addEventListener?.("change", update);
    return () => {
      media.removeEventListener?.("change", update);
    };
  }, []);

  useEffect(() => {
    if (!isCompactViewport && showCompactSheet) {
      setShowCompactSheet(false);
    }
  }, [isCompactViewport, showCompactSheet]);

  useEffect(() => {
    if (!showCompactSheet || typeof document === "undefined") {
      return;
    }
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, [showCompactSheet]);

  if (activeTasks.length === 0 && recentTasks.length === 0) {
    return null;
  }

  const renderTaskCard = (
    task: IntellioTaskRecord,
    section: "active" | "recent",
  ) => (
    <article
      key={task.id}
      data-testid={`intellio-task-card-${task.id}`}
      data-assistant-feature-utility-theme="shared-shell"
      data-assistant-feature-utility-surface="task-card"
      className={`rounded-2xl border p-4 shadow-[0_18px_40px_rgba(36,50,53,0.08)] transition-colors ${cardTone(task)}`}
    >
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-primary/70">
            {section === "active"
              ? task.isNew
                ? "Current task"
                : "Background task"
              : "Recent task"}
          </p>
          <h3 className="mt-1 text-sm font-bold text-slate-900">
            {task.title}
          </h3>
          <div className="mt-2 flex flex-wrap items-center gap-2">
            {task.isNew ? (
              <span className="rounded-full bg-primary px-2.5 py-1 text-[10px] font-semibold uppercase tracking-[0.16em] text-white">
                Just started
              </span>
            ) : null}
            {task.isOptimistic && !task.isNew ? (
              <span className="rounded-full border border-primary/20 bg-primary/5 px-2.5 py-1 text-[10px] font-semibold uppercase tracking-[0.16em] text-primary">
                Syncing live state
              </span>
            ) : null}
            <p className="text-xs text-irongrey/70">
              {task.phaseLabel ?? task.state} · {task.updatedAtLabel}
            </p>
          </div>
        </div>
        <button
          type="button"
          className="rounded-full border border-irongrey/15 px-2.5 py-1 text-[10px] font-semibold uppercase tracking-wide text-irongrey hover:bg-gray-50"
          onClick={() => setSelectedTaskId(task.id)}
          data-testid={`intellio-task-open-${task.id}`}
        >
          Details
        </button>
      </div>

      <div className="mt-3">
        <div className="mb-1 flex items-center justify-between text-[11px] font-medium text-irongrey/75">
          <span>{task.phaseLabel ?? "Working"}</span>
          <span>
            {Math.max(0, Math.min(100, Number(task.progressPercent ?? 0)))}%
          </span>
        </div>
        <div className="h-2 rounded-full bg-gray-100">
          <div
            className={`h-2 rounded-full bg-gradient-to-r ${progressTone(task.state)}`}
            style={{
              width: `${Math.max(6, Math.min(100, Number(task.progressPercent ?? 0)))}%`,
            }}
            data-testid={`intellio-task-progress-${task.id}`}
          />
        </div>
      </div>

      <p className="mt-3 text-sm text-irongrey/85">
        {task.statusMessage ?? task.resultSummary ?? "Task update available."}
      </p>

      {task.artifacts && task.artifacts.length > 0 ? (
        <div className="mt-3 flex flex-wrap gap-2">
          {task.artifacts.slice(0, 2).map((artifact) => (
            <a
              key={artifact.id}
              href={artifact.downloadUrl}
              target="_blank"
              rel="noreferrer"
              className="rounded-full border border-primary/20 bg-primary/5 px-3 py-1.5 text-[11px] font-semibold text-primary hover:bg-primary/10"
              data-testid={`intellio-task-artifact-${artifact.id}`}
            >
              {artifact.label}
            </a>
          ))}
        </div>
      ) : null}

      <div className="mt-3 flex flex-wrap gap-2">
        {task.canConfirm ? (
          <button
            type="button"
            className="rounded-full bg-primary px-3 py-1.5 text-[11px] font-semibold text-white hover:opacity-90"
            onClick={() => onConfirmTask?.(task.id)}
            data-testid={`intellio-task-confirm-${task.id}`}
          >
            Send now
          </button>
        ) : null}
        {task.canCancel ? (
          <button
            type="button"
            className="rounded-full border border-strawberry/20 bg-strawberry/5 px-3 py-1.5 text-[11px] font-semibold text-strawberry hover:bg-strawberry/10"
            onClick={() => onCancelTask?.(task.id)}
            data-testid={`intellio-task-cancel-${task.id}`}
          >
            Cancel
          </button>
        ) : null}
      </div>
    </article>
  );

  const compactShelfNode = isCompactViewport ? (
    <>
      <section
        data-testid="intellio-task-shelf"
        data-assistant-feature-utility-theme="shared-shell"
        data-assistant-feature-utility-surface="task-shelf"
        className="mx-3 mb-3 rounded-[22px] border border-emerald-100 bg-[linear-gradient(180deg,rgba(246,250,248,0.98),rgba(255,255,255,0.98))] px-4 py-3 shadow-[0_20px_48px_rgba(36,50,53,0.08)]"
      >
        <div className="flex items-center justify-between gap-3">
          <div className="min-w-0">
            <p className="text-[11px] font-semibold uppercase tracking-[0.22em] text-primary/70">
              Task lane
            </p>
            <p className="mt-1 truncate text-sm font-semibold text-slate-900">
              {prioritizedActiveTasks.length > 0
                ? `${prioritizedActiveTasks.length} active task${prioritizedActiveTasks.length === 1 ? "" : "s"} running`
                : `${recentTasks.length} recent task${recentTasks.length === 1 ? "" : "s"} available`}
            </p>
            <p className="mt-1 truncate text-[12px] text-irongrey/75">
              {(prioritizedActiveTasks[0] ?? recentTasks[0])?.title ??
                "Background workflows stay attached to this conversation."}
            </p>
          </div>
          <button
            type="button"
            className="shrink-0 rounded-full border border-primary/20 bg-white px-3 py-1.5 text-[11px] font-semibold text-primary transition-colors hover:bg-primary/5"
            onClick={() => setShowCompactSheet(true)}
            data-testid="intellio-task-shelf-open-sheet"
          >
            View tasks
          </button>
        </div>
      </section>

      {showCompactSheet ? (
        <button
          type="button"
          data-passive-click-surface="true"
          data-assistant-feature-utility-theme="shared-shell"
          data-assistant-feature-utility-surface="task-sheet-backdrop"
          className="fixed inset-0 z-[238] bg-slate-900/40"
          aria-label="Close task lane"
          onClick={() => setShowCompactSheet(false)}
          data-testid="intellio-task-shelf-sheet-backdrop"
        />
      ) : null}
      <div
        className={[
          "fixed inset-x-0 bottom-0 z-[239] flex max-h-[min(74dvh,680px)] flex-col rounded-t-[28px] border border-slate-200 bg-white shadow-[0_-20px_56px_rgba(18,38,41,0.22)] transition-transform duration-200",
          showCompactSheet
            ? "translate-y-0"
            : "translate-y-full pointer-events-none",
        ].join(" ")}
        data-testid="intellio-task-shelf-sheet"
        data-assistant-feature-utility-theme="shared-shell"
        data-assistant-feature-utility-surface="task-sheet"
        style={{ paddingBottom: "env(safe-area-inset-bottom, 0px)" }}
      >
        <div className="flex items-center justify-between gap-3 border-b border-slate-100 px-4 py-3">
          <div>
            <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-primary/70">
              Background workflows
            </p>
            <p className="mt-1 text-sm text-irongrey/75">
              Task progress and artifacts stay out of the main chat scroll.
            </p>
          </div>
          <button
            type="button"
            className="rounded-full border border-gray-200 px-3 py-1.5 text-[11px] font-semibold text-irongrey transition-colors hover:bg-gray-50"
            onClick={() => setShowCompactSheet(false)}
            data-testid="intellio-task-shelf-close-sheet"
          >
            Close
          </button>
        </div>
        <div className="min-h-0 flex-1 overflow-y-auto px-4 py-4">
          {prioritizedActiveTasks.length > 0 ? (
            <div className="space-y-3">
              {prioritizedActiveTasks.map((task) =>
                renderTaskCard(task, "active"),
              )}
            </div>
          ) : (
            <div
              className="rounded-2xl border border-dashed border-gray-200 bg-gray-50/70 p-4 text-sm text-irongrey/75"
              data-assistant-feature-utility-theme="shared-shell"
              data-assistant-feature-utility-surface="task-empty-state"
            >
              No active background tasks.
            </div>
          )}
          <div className="mt-4 border-t border-slate-100 pt-4">
            <div className="mb-3 flex items-center justify-between gap-3">
              <p className="text-[11px] font-semibold uppercase tracking-[0.16em] text-irongrey/55">
                Recent history
              </p>
              <button
                type="button"
                className="text-[11px] font-semibold text-primary"
                onClick={onToggleCompletedCollapsed}
                data-testid="intellio-task-history-toggle"
              >
                {completedCollapsed ? "Expand" : "Collapse"}
              </button>
            </div>
            {!completedCollapsed && recentTasks.length > 0 ? (
              <div className="space-y-3">
                {recentTasks.map((task) => renderTaskCard(task, "recent"))}
              </div>
            ) : null}
          </div>
        </div>
      </div>
    </>
  ) : null;

  return (
    <>
      {compactShelfNode ?? (
        <section
          data-testid="intellio-task-shelf"
          data-assistant-feature-utility-theme="shared-shell"
          data-assistant-feature-utility-surface="task-shelf"
          className="mx-3 mb-3 rounded-[24px] border border-emerald-100 bg-[linear-gradient(180deg,rgba(246,250,248,0.98),rgba(255,255,255,0.98))] p-4 shadow-[0_28px_70px_rgba(36,50,53,0.08)]"
        >
          <div className="flex items-start justify-between gap-3">
            <div>
              <p className="text-[11px] font-semibold uppercase tracking-[0.22em] text-primary/70">
                Task lane
              </p>
              <h2 className="mt-1 text-base font-black text-slate-900">
                Background workflows
              </h2>
              <p className="mt-1 text-sm text-irongrey/80">
                Tasks keep running while the conversation stays live.
              </p>
            </div>
            <div className="rounded-full bg-white px-3 py-1 text-[11px] font-semibold text-irongrey shadow-sm">
              {prioritizedActiveTasks.length} active
            </div>
          </div>

          {prioritizedActiveTasks.length > 0 ? (
            <div className="mt-4 grid gap-3 lg:grid-cols-2">
              {prioritizedActiveTasks.map((task) =>
                renderTaskCard(task, "active"),
              )}
            </div>
          ) : (
            <div
              className="mt-4 rounded-2xl border border-dashed border-gray-200 bg-white/70 p-4 text-sm text-irongrey/75"
              data-assistant-feature-utility-theme="shared-shell"
              data-assistant-feature-utility-surface="task-empty-state"
            >
              No active background tasks.
            </div>
          )}

          <div className="mt-5">
            <button
              type="button"
              className="flex w-full items-center justify-between rounded-2xl border border-gray-200 bg-white/90 px-4 py-3 text-left"
              onClick={onToggleCompletedCollapsed}
              data-testid="intellio-task-history-toggle"
            >
              <span>
                <span className="block text-[11px] font-semibold uppercase tracking-[0.18em] text-irongrey/55">
                  Recent history
                </span>
                <span className="mt-1 block text-sm font-semibold text-slate-900">
                  {recentTasks.length} completed or closed task
                  {recentTasks.length === 1 ? "" : "s"}
                </span>
              </span>
              <span className="text-xs font-semibold text-primary">
                {completedCollapsed ? "Expand" : "Collapse"}
              </span>
            </button>
            {!completedCollapsed && recentTasks.length > 0 ? (
              <div className="mt-3 grid gap-3 lg:grid-cols-2">
                {recentTasks.map((task) => renderTaskCard(task, "recent"))}
              </div>
            ) : null}
          </div>
        </section>
      )}

      {selectedTask ? (
        <div
          data-passive-click-surface="true"
          className="fixed inset-0 z-[240] flex items-center justify-center bg-slate-900/40 px-4"
          data-testid="intellio-task-detail-backdrop"
          onClick={() => setSelectedTaskId(null)}
        >
          <div
            className="w-full max-w-2xl rounded-[28px] bg-white p-6 shadow-[0_35px_90px_rgba(18,38,41,0.32)]"
            onClick={(event) => event.stopPropagation()}
          >
            <div className="flex items-start justify-between gap-4">
              <div>
                <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-primary/70">
                  {selectedTask.phaseLabel ?? selectedTask.state}
                </p>
                <h3 className="mt-1 text-xl font-black text-slate-900">
                  {selectedTask.title}
                </h3>
                <p className="mt-2 text-sm text-irongrey/80">
                  {selectedTask.updatedAtLabel}
                </p>
              </div>
              <button
                type="button"
                className="rounded-full border border-gray-200 px-3 py-1 text-xs font-semibold text-irongrey"
                onClick={() => setSelectedTaskId(null)}
              >
                Close
              </button>
            </div>

            <div className="mt-5">
              <div className="mb-1 flex items-center justify-between text-[11px] font-medium text-irongrey/75">
                <span>{selectedTask.phaseLabel ?? "Working"}</span>
                <span>
                  {Math.max(
                    0,
                    Math.min(100, Number(selectedTask.progressPercent ?? 0)),
                  )}
                  %
                </span>
              </div>
              <div className="h-2 rounded-full bg-gray-100">
                <div
                  className={`h-2 rounded-full bg-gradient-to-r ${progressTone(selectedTask.state)}`}
                  style={{
                    width: `${Math.max(6, Math.min(100, Number(selectedTask.progressPercent ?? 0)))}%`,
                  }}
                />
              </div>
            </div>

            <p className="mt-5 text-sm leading-6 text-irongrey/90">
              {selectedTask.resultSummary ??
                selectedTask.statusMessage ??
                "Task details unavailable."}
            </p>

            {selectedTask.artifacts && selectedTask.artifacts.length > 0 ? (
              <div className="mt-5 space-y-2">
                <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-irongrey/55">
                  Artifacts
                </p>
                <div className="flex flex-wrap gap-2">
                  {selectedTask.artifacts.map((artifact) => (
                    <a
                      key={artifact.id}
                      href={artifact.downloadUrl}
                      target="_blank"
                      rel="noreferrer"
                      className="rounded-full border border-primary/20 bg-primary/5 px-3 py-1.5 text-[11px] font-semibold text-primary hover:bg-primary/10"
                    >
                      {artifact.label}
                    </a>
                  ))}
                </div>
              </div>
            ) : null}
          </div>
        </div>
      ) : null}
    </>
  );
};
